import Vue from '../../../lib/utils/vue'
import scopedStyleAttrsMixin from '../../../lib/mixins/scoped-style-attrs'
import { isFunction, isUndefinedOrNull } from '../../../lib/utils/inspect'
import { CvtPopper } from './CvtPopper'
import { styled } from '@egoist/vue-emotion'
import { VueEmotion } from '@egoist/vue-emotion'
import * as getters from '../../../../builder/src/js/store/modules/getters'
import { mapGetters, mapState } from '../../../../builder/node_modules/vuex'
let FontFamily, Spacing, Colors
let textFontWeight, textLineHeight, textFontSize, backgroundColor, color, elevation

Vue.use(VueEmotion)

const UTIL = {
  textColor (props) {
    return props.specialBoxShadowForPopup !== '' || props.themeing == 'light'
      ? color({ themeing: 'dark' }, 500)
      : color({ themeing: 'dark' }, 100)
  },
  caretBorderColor (props, direction) {
    return props.specialBoxShadowForPopup !== '' ?
      `border-${direction}-color: ${Colors.BASIC['WHITE']};`
      : props.themeing == 'dark'
      ? `border-${direction}-color: ${Colors.BASIC[1100]};`
      : `border-${direction}-color: ${Colors.BASIC[100]};`
  }
}

const getTooltip = () => styled('div')`
  display: flex;
  opacity: 1;
  outline: 0;
  position: absolute;
  z-index: 2021;
  margin: 0;
  font-family: ${FontFamily.DEFAULT};
  text-align: left;
  text-decoration: none;
  text-shadow: none;
  text-transform: none;
  letter-spacing: normal;
  word-break: normal;
  word-spacing: normal;
  white-space: normal;
  line-break: auto;
  word-wrap: break-word;
  padding: ${Spacing.SPACING_8}px;

  ${(props) => {
    let fontWeight = textFontWeight()
    let lineHeight = textLineHeight('xl')
    let fontSize = textFontSize('sm')

    return fontSize + fontWeight + lineHeight
  }}
  &.fade:not(.show) {
    opacity: 0;
  }

  &.show {
    opacity: 1;
  }

  // Disabled pointer events when in 'noninteractive' mode to hide
  // the tooltip when the user hovers over its content
  &.noninteractive {
    pointer-events: none;
  }

  &[data-popper-placement='left'] {
    .arrow {
      right: 0;
      &:before {
        right: 0;
        border-width: ${Spacing.SPACING_8}px 0px ${Spacing.SPACING_8}px
          ${Spacing.SPACING_8}px;
        ${(props) => UTIL.caretBorderColor(props, 'left')}
      }
    }
  }

  &[data-popper-placement='top'] {
    .arrow {
      bottom: 0;
      &:before {
        bottom: 0;
        border-width: ${Spacing.SPACING_8}px ${Spacing.SPACING_8}px 0;
        ${(props) => UTIL.caretBorderColor(props, 'top')}
      }
    }
  }

  &[data-popper-placement='right'] {
    .arrow {
      left: 0;
      &:before {
        left: 0;
        border-width: ${Spacing.SPACING_8}px ${Spacing.SPACING_8}px
          ${Spacing.SPACING_8}px 0;
        ${(props) => UTIL.caretBorderColor(props, 'right')}
      }
    }
  }

  &[data-popper-placement='bottom'] {
    .arrow {
      top: 0;
      &:before {
        top: 0;
        border-width: 0 ${Spacing.SPACING_8}px ${Spacing.SPACING_8}px;
        ${(props) => UTIL.caretBorderColor(props, 'bottom')}
      }
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    > * {
      margin: 0 8px;
    }
  }
`

const getArrow = () => styled('div')`
  margin: 0;
  display: block;
  width: 16px;
  height: 16px;

  &:before {
    position: absolute;
    content: '';
    border-color: transparent;
    border-style: solid;
  }
`

const getTooltipInner = () => styled('div')`
  max-width: 80vw;
  overflow: auto;
  max-height: 50vh;
  text-align: center;
  ${(props) => {
    let bgColor = props.specialBoxShadowForPopup !== '' ? `background-color:#FFFFFF;` : backgroundColor(props, 1100)
    let innerTextColor = UTIL.textColor(props)
    let shadow = props.specialBoxShadowForPopup !== '' ? `box-shadow: ${props.specialBoxShadowForPopup};` : elevation(props, 500)
    let padding = `padding: ${props.innerPadding}px;`
    let borderRadius = `border-radius: ${props.borderRadius}px;`
    return bgColor + innerTextColor + shadow + padding + borderRadius
  }}
`

const NAME = 'TooltipTemplate'

// @vue/component
export const TooltipTemplate = /*#__PURE__*/ Vue.extend({
  name: NAME,
  extends: CvtPopper,
  mixins: [scopedStyleAttrsMixin],
  props: {
    // Other non-reactive (while open) props are pulled in from Popper
    id: {
      type: String,
      default: null
    },
    html: {
      // Used only by the directive versions
      type: Boolean,
      default: false
    },
    innerPadding: {
      type: Number,
      default: 16
    },
    borderRadius: {
      type: Number,
      default: 8
    }
  },
  data () {
    // We use data, rather than props to ensure reactivity
    // Parent component will directly set this data
    return {
      title: '',
      content: '',
      mode: 'DARK',
      customClass: null,
      interactive: false
    }
  },
  created () {
    FontFamily = this.FontFamily
    Spacing = this.Spacing
    Colors = this.Colors
    textFontWeight = this.textFontWeight
    color = this.textColor
    backgroundColor = this.backgroundColor
    textFontSize = this.textFontSize
    textLineHeight = this.textLineHeight
    elevation = this.elevation
  },
  computed: {
    ...mapGetters('globalTheme', {
      textFontWeight: getters.GLOBAL_STYLE_FONT_WEIGHT,
      textColor: getters.GLOBAL_STYLE_COLOR,
      backgroundColor: getters.GLOBAL_STYLE_BACKGROUND_COLOR,
      textFontSize: getters.GLOBAL_STYLE_FONT_SIZE,
      textLineHeight: getters.GLOBAL_STYLE_LINE_HEIGHT,
      elevation: getters.GLOBAL_STYLE_ELEVATION
    }),
    ...mapState('globalTheme', {
      FontFamily: ({ globalTheme }) => globalTheme.FontFamily,
      Spacing: ({ globalTheme }) => globalTheme.Spacing,
      Colors: ({ globalTheme }) => globalTheme.Colors,
      specialBoxShadowForPopup: ({ globalTheme }) => globalTheme?.specialBoxShadowForPopup !== undefined ? globalTheme.specialBoxShadowForPopup : '',
    }),
    templateType () {
      return 'tooltip'
    },
    templateClasses () {
      return [
        {
          // Disables pointer events to hide the tooltip when the user
          // hovers over its content
          noninteractive: !this.interactive,
          [`b-${this.templateType}-${this.variant}`]: this.variant,
          // `attachment` will come from BVToolpop
          [`bs-${this.templateType}-${this.attachment}`]: this.attachment
        },
        this.customClass
      ]
    },
    templateAttributes () {
      return {
        id: this.id,
        role: 'tooltip',
        tabindex: '-1',
        // Add the scoped style data attribute to the template root element
        ...this.scopedStyleAttrs
      }
    },
    templateListeners () {
      // Used for hover/focus trigger listeners
      return {
        mouseenter: (evt) => {
          /* istanbul ignore next: difficult to test in JSDOM */
          this.$emit('mouseenter', evt)
        },
        mouseleave: (evt) => {
          /* istanbul ignore next: difficult to test in JSDOM */
          this.$emit('mouseleave', evt)
        },
        focusin: (evt) => {
          /* istanbul ignore next: difficult to test in JSDOM */
          this.$emit('focusin', evt)
        },
        focusout: (evt) => {
          /* istanbul ignore next: difficult to test in JSDOM */
          this.$emit('focusout', evt)
        }
      }
    }
  },
  methods: {
    renderTemplate (h) {
      // Title can be a scoped slot function
      const $title = isFunction(this.title)
        ? this.title({})
        : isUndefinedOrNull(this.title)
          ? h()
          : this.title

      // Directive versions only
      const domProps =
        this.html && !isFunction(this.title) ? { innerHTML: this.title } : {}

      const Tooltip = getTooltip()
      const TooltipInner = getTooltipInner()
      const Arrow = getArrow()

      return (
        <Tooltip
          {...this.templateAttributes}
          {...this.templateListeners}
          themeing={this.color}
          mode={this.mode}
          direction={this.attachment}
          specialBoxShadowForPopup={this.specialBoxShadowForPopup}
        >
          <TooltipInner
            innerPadding={this.innerPadding}
            borderRadius={this.borderRadius}
            {...domProps}
            mode={this.mode}
            themeing={this.color}
            specialBoxShadowForPopup={this.specialBoxShadowForPopup}
          >
            {$title}
          </TooltipInner>
          <Arrow
            ref='arrow'
            class='arrowtooltip arrow'
            direction={this.attachment}
            themeing={this.color}
            mode={this.mode}
          ></Arrow>
        </Tooltip>
      )
    }
  }
})
